<template>
  <sui-card class="state-card">
    <sui-card-content class="compact">
      <b>Source code</b>
    </sui-card-content>
    <a class="content link" :href="service.source.url || null" target="_blank" title="Open source" data-goal="SERVICE_PAGE_SOURCES_OPEN">
      <template v-if="service.source.type === 'git'">
        <template v-if="matchGithub">
          <i class="github huge fitted icon" />
          <b>GitHub</b>
        </template>
        <template v-if="matchGitlab">
          <i class="gitlab huge fitted icon" />
          <b>GitLab</b>
        </template>
        <template v-if="matchBitbucket">
          <i class="bitbucket huge fitted icon" />
          <b>Bitbucket</b>
        </template>
        <template v-if="!matchGithub && !matchGitlab && !matchBitbucket">
          <i class="git huge fitted icon" />
          <b>Repository</b>
        </template>
      </template>
      <template v-if="service.source.url && service.source.type === 'download'">
        <i class="cloud download huge fitted icon" />
        <b>Downloaded</b>
      </template>
      <template v-if="service.source.type === 'upload'">
        <i class="file archive huge fitted icon" />
        <b>Uploaded</b>
      </template>
      <template v-if="!service.source.url && service.source.type !== 'upload'">
        <i class="minus circle huge fitted icon" />
        <b>Empty</b>
      </template>
    </a>
    <label class="ui fluid bottom attached mini button" :class="{loading: uploading, disabled: isUploadDisabled}" v-show="service.source.type === 'upload'">
      <input type="file" ref="file" accept=".tar.bz2, .tar.gz, .tar, .zip" @change="upload()"/>
      Upload
    </label>
    <sui-button-group class="fluid" size="mini" attached="bottom" :disabled="isUpdateDisabled" v-show="service.source.type !== 'upload'">
      <sui-button icon="cog" :disabled="inProgress || !actions.serviceUpdate" data-goal="SERVICE_PAGE_SOURCES_EDIT" @click.prevent="$modals.updateService.open(service, 'updatingSource')" />
      <sui-dropdown ref="actions" text="Update" icon="dropdown" :disabled="inProgress || !actions.serviceUpdate" button>
        <sui-dropdown-menu>
          <sui-dropdown-item v-for="action in actionsForApply" :key="action.value" :disabled="action.disabled" @click.stop="action.checked = !action.checked">
            <sui-checkbox :label="action.text" v-model="action.checked" @click.stop />
          </sui-dropdown-item>
          <sui-dropdown-header>
            <sui-button color="blue" size="mini" fluid :disabled="!checkedActions.length" data-goal="SERVICE_PAGE_SOURCES_UPDATE" @click="doSelectedActions()">Update</sui-button>
          </sui-dropdown-header>
        </sui-dropdown-menu>
      </sui-dropdown>
    </sui-button-group>
    <sui-dimmer :active="hasError" @click="error = null">
      <p><b>Error!</b></p>
      <p><small>{{error}}</small></p>
    </sui-dimmer>
  </sui-card>
</template>

<script>
  import {mapGetters} from 'vuex'
  import api from '@/api'
  import utils from '@/utils'

  export default {
    name: 'ServiceSourceCard',
    props: {
      service: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        error: null,
        uploading: false,
        actionsForApply: [
          {value: 'REBUILD', text: 'Update global dependencies', checked: false, disabled: false},
          {value: 'REDEPLOY', text: 'Update local dependencies', checked: this.service._needRedeploy, disabled: false},
          {value: 'SYNC_SOURCES', text: 'Update sources', checked: true, disabled: false},
          {value: 'RSYNC_GIT', text: 'Include .git folder', checked: false, disabled: this.service.source.type !== 'git'},
        ],
      }
    },
    computed: {
      ...mapGetters(['actionsById', 'config']),
      actions() {
        return this.actionsById[this.service.id]
      },
      hasError() {
        return Boolean(this.error)
      },
      isDeployed() {
        return this.service._isDeployed
      },
      inProgress() {
        return this.service._inProgress
      },
      matchGitlab() {
        return utils.matchGitlab(this.service.source.url)
      },
      matchGithub() {
        return utils.matchGithub(this.service.source.url)
      },
      matchBitbucket() {
        return utils.matchBitbucket(this.service.source.url)
      },
      isUploadDisabled() {
        return this.inProgress || this.uploading
      },
      isUpdateDisabled() {
        return !this.service.constructor.methods.actions || !this.isDeployed || this.inProgress
      },
      checkedActions() {
        return this.actionsForApply.filter(action => action.checked).map(action => action.value)
      },
    },
    watch: {
      actionsForApply: {
        handler(newActions) {
          if (newActions[1].checked) {
            newActions[2].checked = true
            newActions[2].disabled = true
          } else {
            newActions[2].disabled = false
          }
        },
        deep: true,
        immediate: true,
      },
    },
    methods: {
      doSelectedActions() {
        this.service.applyActions(this.checkedActions).catch(error => {
          this.$emit('error', error.message)
        })
      },
      upload() {
        const archive = this.$refs.file.files[0]
        if (!archive) {
          this.error = 'File is not selected'
          return
        }
        const isArchive = utils.isArchiveUrl(archive.name)
        if (!isArchive) {
          this.error = 'Available archive types: .tar.bz2, .tar.gz, .tar, .zip'
          return
        }
        const uploadSize = this.config.uploadSize
        const goodSize = archive.size < uploadSize
        if (!goodSize) {
          this.error = 'Maximum file size: ' + utils.prettyBytes(uploadSize)
          return
        }
        this.uploading = true
        utils.reachGoal('SERVICE_PAGE_SOURCES_UPLOAD')
        api.upload(archive)
          .finally(() => {
            this.uploading = false
            this.$refs.file.value = ''
          })
          .then(response => {
            this.service.update({source: {type: 'upload', url: response.data.result}})
          })
          .catch(error => {
            this.error = error.message
          })
      }
    },
  }
</script>

<style scoped>
  .ui.inline.loader { width: 1em!important; height: 1em!important; font-size: 4em!important; }
  .ui.inline.loader:before { width: 100%; height: 100%; left: 0; margin: 0; }
  .ui.inline.loader:after { width: 100%; height: 100%; left: 0; margin: 0; }
  .ui.dimmer { background-color: rgba(150, 50, 50, .8)!important; }
  .ui.dimmer p { margin-bottom: .3em; }

  .dropdown { text-align: center!important; }
</style>
